import {
    ReadonlyVec2,
    ReadonlyVec4,
    mat4,
    quat,
    vec2,
    vec3,
    vec4,
} from 'gl-matrix';
import { gl } from '../../Engine';
import { SignedDistanceField } from '../../core/models/SignedDistanceField';
import { ShaderProgram } from '../../core/utils/ShaderProgram';
import { UniformMatrix } from '../../core/utils/uniforms/UniformMatrix';
import { UniformVec2 } from '../../core/utils/uniforms/UniformVec2';
import { UniformVec4 } from '../../core/utils/uniforms/UniformVec4';
import { RotatedBoundingBox } from '../../data/common/RotatedBoundingBox';
import { CustomTargetObject } from '../../data/common/customs/CustomTargetObject';
import { ContourTexture } from '../../data/structure/ContourTexture';
import { TextureView } from '../../view/TextureView';
import { PlaneView } from '../../view/views/PlaneView';

//TODO: imports below are to be removed
import { ImageObject } from '~/engine/data/image/ImageObject';
import { BoundingBox } from '../../data/common/BoundingBox';
import { Grid } from '~/engine/data/common/Grid';
//import { ViewManager } from '../../view/ViewManager';
//import { contentToBitmap } from '../../util/gl/GLUtilities';

/**
 * {@link DrawObject} is a {@link CustomTargetObject} that is used to draw on the {@link SliceSource} space.\
 * It is used to draw on the {@link SliceSource} space and then create a {@link ContourTexture} from the drawn content.\
 * The {@link ContourTexture} is then used to create a {@link SignedDistanceField} that is used to create a geometry that is used to render the drawn content.
 *
 * {@link DrawObject} is well aligned with the {@link SliceSource} space.\
 * It covers a volume defined by {@link Rectangle3D}. And it has a {@link sdf} texture that can be used for drawing operations.
 */
export class DrawObject extends CustomTargetObject {
    /**
     * Value that determines the width of the outline in pixels.\
     * This is used by the {@link shader} when rendering the {@link DrawObject}.
     */
    private static readonly LINE_WIDTH: number = 1;

    /**
     * {@link RotatedBoundingBox} that defines the volume this {@link DrawObject} covers.\
     * The {@link RotatedBoundingBox.rotation rotation} is equal to that of the {@link target}.\
     * The {@link RotatedBoundingBox.center position} and {@link RotatedBoundingBox.size size} are calculated based on the {@link Rectangle3D} and {@link target} passed to the {@link constructor}.\
     * So {@link RotatedBoundingBox} is well aligned with {@link target} and covers the volume of {@link Rectangle3D}.
     */
    private readonly _boundingBox: RotatedBoundingBox;

    /**
     * Value that determines the planar distance between neighboring pixels along x and y -axes in millimeters.\
     * The unit is mm/px.
     */
    protected readonly resolution: ReadonlyVec2;

    /**
     * The {@link ReadonlyVec2} describing the size of this {@link DrawObject} in pixels along x and y -axes.
     */
    protected readonly _planarPixelSize: vec2;

    /**
     * {@link DrawObjectOutlineShader} used to render the {@link DrawObject}.\
     * This works similarly to {@link ContourRenderer}, rendering the outline of the {@link DrawObject}.\
     * It expects the {@link sdf} to be an actual sdf, where the outline is rendered where the pixel values turn 0.5.
     */
    private readonly shader: DrawObjectOutlineShader;

    /**
     * Readonly {@link vec4} describing the color of the {@link DrawObject}.\
     * This is used by the {@link shader} when {@link render rendering} the {@link DrawObject}'s outline.
     */
    protected readonly color: vec4;

    /**
     * {@link mat4} Matrix defining transformation from PatientSpace coordinates to ImageSpace coordinates.
     */
    protected readonly patientToImage: mat4;

    private readonly _image: ImageObject;

    /**
     * {@link DrawObject}'s constructor.\
     * This computes the optimal {@link _boundingBox BoundingBox} based on the {@link target} and {@link viewport}.\
     * It also defines the {@link patientToImage} transformation matrix, which stays mostly constant.
     * @param target {@link SliceSource} defining the space that the {@link DrawObject} should align with.
     * @param resolution
     * @param viewport
     * @param normalDistance
     */
    public constructor(
        target: ImageObject,
        resolution: ReadonlyVec2,
        viewportBoundingBox: BoundingBox,
        color: ReadonlyVec4
    ) {
        super(target);
        this._image = target;
        this.resolution = vec2.clone(resolution);
        this._boundingBox = new RotatedBoundingBox();
        this._boundingBox.rotate(target.grid.getOrientation());
        this._boundingBox.expandWithPoint(viewportBoundingBox.min!);
        this._boundingBox.expandWithPoint(viewportBoundingBox.max!);

        this.patientToImage = mat4.clone(
            this._boundingBox.toImageSpaceTransformation()
        );
        this.patientToImage[12] =
            this.patientToImage[13] =
            this.patientToImage[14] =
                0.0;
        this._planarPixelSize = vec2.fromValues(
            Math.ceil(this._boundingBox.size[0] / this.resolution[0]),
            Math.ceil(this._boundingBox.size[1] / this.resolution[1])
        );
        const gridTransformation = mat4.fromRotationTranslation(
            mat4.create(),
            quat.fromMat3(quat.create(), this._imageGrid.getOrientation()),
            vec3.transformMat3(
                vec3.create(),
                this._boundingBox.min!,
                this._boundingBox.rotation
            )
        );

        this._grid = new Grid(
            this._planarPixelSize[0],
            this._planarPixelSize[1],
            Math.ceil(this._boundingBox.size[2] / target.parent.grid.resZ),
            this.resolution[0],
            this.resolution[0],
            target.parent.grid.resZ,
            gridTransformation
        );
        this.sdf = new SignedDistanceField(this._grid.getSize());
        this.color = vec4.clone(color);
        this.shader = new DrawObjectOutlineShader();
    }

    /**
     * Method to get the {@link ReadonlyVec2 size} of this {@link DrawObject} in pixels along x and y -axes.
     */
    public get planarPixelSize(): ReadonlyVec2 {
        return this._planarPixelSize;
    }

    /**
     * Method to get the number of slices of this {@link DrawObject}.\
     */
    public get numberOfSlices(): number {
        return this._grid.sizeZ;
    }

    /**
     * @override abstract {@link CustomTargetObject.render render}
     * Method to render the outlines of the {@link DrawObject} onto the {@link TextureView}.\
     * This method is called by the {@link DrawObject.render render} method of the {@link DrawObject}.
     * @param view {@link TextureView} on which the {@link DrawObject} shall be rendered.
     */
    public render(view: TextureView): void {
        if (view instanceof PlaneView) {
            const trans = mat4.fromRotationTranslation(
                mat4.create(),
                quat.fromMat3(quat.create(), this._grid.getOrientation()),
                vec3.create()
            );
            const vao = this._boundingBox.crossSection(
                view.projectionViewMatrix,
                trans
            );
            if (vao && this.sdf) {
                gl.enable(gl.BLEND);
                gl.blendEquation(gl.FUNC_ADD);
                gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
                this.shader.start();
                this.shader.relativeDist.loadVec2(
                    vec2.fromValues(
                        DrawObject.LINE_WIDTH / view.size[0],
                        DrawObject.LINE_WIDTH / view.size[1]
                    )
                );

                const viewToPatient = mat4.invert(
                    mat4.create(),
                    view.projectionViewMatrix
                );
                //remove translation
                viewToPatient[12] = viewToPatient[13] = viewToPatient[14] = 0.0;
                this.shader.colorMask.loadVec4(this.color);

                this.shader.viewToTexture.loadMatrix(
                    mat4.multiply(
                        mat4.create(),
                        this.patientToImage,
                        viewToPatient
                    )
                );
                this.sdf.bind();
                vao.bind(0, 1);
                gl.drawElements(
                    gl.TRIANGLES,
                    vao.indexCount,
                    gl.UNSIGNED_INT,
                    0
                );
                vao.unbind(0, 1);
                vao.delete();
                this.shader.stop();
                gl.disable(gl.BLEND);
            }
        }
    }

    public toContourTexture(): ContourTexture | undefined {
        // read the SDF pixels into CPU memory
        const sdfBytes = this.signedDistanceField.toArray();

        const min = vec3.fromValues(
            Number.MAX_VALUE,
            Number.MAX_VALUE,
            Number.MAX_VALUE
        );
        const max = vec3.fromValues(
            Number.MIN_VALUE,
            Number.MIN_VALUE,
            Number.MIN_VALUE
        );

        for (let z = 0; z < this._grid.sizeZ; z++) {
            let idx = 0;
            for (let y = 0; y < this._grid.sizeY; y++) {
                for (let x = 0; x < this._grid.sizeX; x++) {
                    if (sdfBytes[z][idx] > 0) {
                        min[0] = Math.min(min[0], x);
                        min[1] = Math.min(min[1], y);
                        min[2] = Math.min(min[2], z);
                        max[0] = Math.max(max[0], x);
                        max[1] = Math.max(max[1], y);
                        max[2] = Math.max(max[2], z);
                    }
                    idx++;
                }
            }
        }

        const sourceRowLen = max[0] - min[0];

        // add empty slices on top and bottom
        const sdfPixelSize = vec3.fromValues(
            sourceRowLen + 1,
            max[1] - min[1] + 1,
            max[2] - min[2] + 3
        );

        // patient space coordinates for the bounding box
        const minCoords = this._grid.toWorldSpace(
            vec3.add(vec3.create(), min, vec3.fromValues(0, 0, -0.5))
        );
        const maxCoords = this._grid.toWorldSpace(
            vec3.add(vec3.create(), max, vec3.fromValues(1, 1, 1.5))
        );

        const bb = new RotatedBoundingBox(
            new BoundingBox(minCoords, maxCoords),
            this._grid.getOrientation()
        );

        const sdf = new SignedDistanceField(sdfPixelSize);
        const contourTexture = new ContourTexture(this._image.parent.grid, bb);
        contourTexture.reset(bb, sdf);

        // copy the cropped SDF to the new SDF, offsetting by 1 to account for the margins
        const sliceArray = new Uint8Array(sdfPixelSize[0] * sdfPixelSize[1]);
        for (let z = min[2]; z <= max[2]; z++) {
            let targetIdx = 0;
            let sourceIdx = min[0] + min[1] * this.signedDistanceField.size[0];
            for (let y = min[1]; y <= max[1]; y++) {
                sliceArray.set(
                    sdfBytes[z].subarray(sourceIdx, sourceIdx + sourceRowLen),
                    targetIdx
                );
                targetIdx += sdfPixelSize[0];
                sourceIdx += this.signedDistanceField.size[0];
            }
            sdf.arrayToTextureSlice(sliceArray, z - min[2] + 1);
        }

        return contourTexture;
    }

    /**
     * {@link RotatedBoundingBox} that defines the volume this {@link DrawObject} covers.\
     * The {@link RotatedBoundingBox.rotation rotation} is equal to that of the {@link target}.\
     * The {@link RotatedBoundingBox.center position} and {@link RotatedBoundingBox.size size} are calculated based on the {@link Rectangle3D} and {@link target} passed to the {@link constructor}.\
     * So {@link RotatedBoundingBox} is well aligned with {@link target} and covers the volume of {@link Rectangle3D}.
     */
    public get boundingBox(): RotatedBoundingBox {
        return this._boundingBox;
    }

    /**
     * Method to get the {@link SignedDistanceField} of this {@link DrawObject}.\
     * This is the {@link SignedDistanceField} on which the {@link DrawObject} is drawn.
     * @throws An {@link Error} if called before the initialization.
     */
    public get signedDistanceField(): Readonly<SignedDistanceField> {
        if (!this.sdf) {
            throw new Error(
                `Attempt to access signed distance field before initialization.`
            );
        }
        return this.sdf;
    }

    /**
     * Method to {@link delete} this {@link DrawObject}.\
     * This method deletes the {@link shader} and calls the {@link CustomTargetObject super} {@link CustomTargetObject.delete delete}.
     * @returns True to indicate that the {@link DrawObject} was deleted.
     */
    public delete() {
        this.shader.delete();
        super.delete();
    }
}

/**
 * {@link ShaderProgram} used to render the {@link DrawObject}.\
 * This works similarly to {@link ContourRenderer}, rendering the outline of the {@link DrawObject}.\
 * It expects the {@link sdf} to be an actual sdf, where the outline is rendered where the pixel values turn 0.5.
 */
class DrawObjectOutlineShader extends ShaderProgram {
    /**
     * Vertex shader content of the {@link DrawObjectOutlineShader}.\
     * It expects two input variables:
     * - position: vec2
     * - textureCoords: vec3
     *
     * Those values represent the intersection points between the {@link DrawObject} and the {@link PlaneView}.\
     * It outputs the 'textureCoords' to the {@link FRAGMENT_CONTENT fragment shader} without changes as 'pass_textureCoordinates'.
     */
    private static readonly VERTEX_CONTENT = `#version 300 es
    
    precision highp float;

    in vec2 position;
    in vec3 textureCoords;

    out vec3 pass_textureCoordinates;

    void main(){
            
            gl_Position = vec4(position, 0.0, 1.0);
    
            pass_textureCoordinates = textureCoords;
    }
    `;

    /**
     * Fragment shader content of the {@link DrawObjectOutlineShader}.\
     * It expects a single input variable:
     * - pass_textureCoordinates: vec3, which defines the texture coordinates on the {@link sdf} texture.
     *
     * It also expects the following uniforms:
     * - colorMask: vec4, which defines the color of the outline.
     * - sampler: sampler3D, which is the {@link sdf} texture.
     * - viewToTexture: mat4, which is the transformation matrix from the {@link PlaneView} to the {@link sdf} texture.
     * - relativeDist: vec2, which defines the relative distance between neighboring pixels along x and y -axes.
     *
     * It outputs the color of the outline as 'outColor'.
     *
     * For each fragment, the fragment shader checks if the neighboring pixels are within the {@link sdf} or not.\
     * If the value of any of the neighbors is on the different side of the 0.5 threshold, the fragment is within the outline and the color of the outline is outputted.\
     * All the fragments not fulfilling that criteria are discarded.
     */
    private static readonly FRAGMENT_CONTENT = `#version 300 es

    precision highp float;
    precision highp sampler3D;

    in vec3 pass_textureCoordinates;

    out vec4 outColor;

    uniform vec4 colorMask;
    uniform sampler3D sampler;

    const float diagonal = 0.70710678118;
    uniform mat4 viewToTexture;
    uniform vec2 relativeDist;

    bool isWithin(float x, float y) {
        vec4 rotated = viewToTexture * vec4(x * relativeDist.x, y * relativeDist.y, 0.0, 1.0);
        vec3 dest = pass_textureCoordinates + rotated.xyz;
        if(dest.x <= 0.0 || dest.x >= 1.0 || dest.y <= 0.0 || dest.y >= 1.0 || dest.z <= 0.0 || dest.z >= 1.0){
            return false;
        }
        return texture(sampler, dest).r >= 0.5;
    }

    void main(){
        bool within = texture(sampler, pass_textureCoordinates).r >= 0.5;

        if(
        isWithin(-diagonal, -diagonal) != within ||
        isWithin(0.0, -1.0) != within ||
        isWithin(diagonal, -diagonal) != within ||
        isWithin(-1.0, 0.0) != within ||
        isWithin(1.0, 0.0) != within ||
        isWithin(-diagonal, diagonal) != within ||
        isWithin(0.0, 1.0) != within ||
        isWithin(diagonal, diagonal) != within){
            outColor = colorMask;
        } else {
            //outColor = vec4(0.0, 0.3, 0.1, 0.6);
            discard;
        }
    }
    `;

    /**
     * {@link UniformVec4} defining the color of the outline.
     */
    public readonly colorMask: UniformVec4;

    /**
     * {@link UniformMatrix} defining the transformation matrix from the {@link PlaneView} to the {@link sdf} texture.
     */
    public readonly viewToTexture: UniformMatrix;

    /**
     * {@link UniformVec2} defining the relative distance between neighboring pixels along x and y -axes.\
     * This relative dist is relative to textureSize. Meaning that for 1 pixel wide borders, the relativeDist is 1 / textureSize.
     */
    public readonly relativeDist: UniformVec2;

    /**
     * {@link DrawObjectOutlineShader} constructor.\
     * It calls the {@link ShaderProgram super} {@link ShaderProgram.constructor constructor} with the {@link VERTEX_CONTENT} and {@link FRAGMENT_CONTENT} as arguments.\
     * It also defines the input variables of the vertex shader as 'position' and 'textureCoords'.\
     * It then initializes the {@link colorMask}, {@link viewToTexture} and {@link relativeDist} uniforms and stores their locations.
     */
    constructor() {
        super(
            DrawObjectOutlineShader.VERTEX_CONTENT,
            DrawObjectOutlineShader.FRAGMENT_CONTENT,
            'position',
            'textureCoords'
        );
        this.colorMask = new UniformVec4('colorMask');
        this.viewToTexture = new UniformMatrix('viewToTexture');
        this.relativeDist = new UniformVec2('relativeDist');
        this.storeUniformLocations(
            this.colorMask,
            this.viewToTexture,
            this.relativeDist
        );
    }
}
